import { useRef, useState } from "react";
import { Text, View } from "react-native";
import { MedicationReportModel } from "app/services/api/types/medications/models/MedicationReportModel";
import { ColumnModel } from "app/services/api/types/common/models/ColumnModel";
import TableList from "app/components/organisms/TableList";
import Pagination from "ui/molecules/pagination/Pagination";
import MedicationLogContent from "../organisms/MedicationLogContent";
import MedicationTimeContent from "../organisms/MedicationTimeContent";
import CardItem from "app/components/organisms/CardItem";
import ReportCardContent from "../organisms/ReportCardContent";
import { getFullName } from "ui/utils/nameUtil";
import { useMedicationLogsWithOffline } from "../../offline/hooks/useMedicationLogsWithOffline";
import {
  MedicationAdministrationStatusEnum,
  statusToColourClassMap,
} from "../../../services/api/types/medications/enums/MedicationAdministrationStatusEnum";
import { snakeToSentence } from "ui/utils/textUtils";
import { FilterButton } from "ui/atoms/button/FilterButton";
import {
  MedicationLogsFilterModal,
  MedicationLogsFilterModalRef,
} from "./MedicationLogsFilterModal";
import { MedicationLogsFilterForm } from "../hooks/useMedicationLogsFilterStorage";
import { ResidentsPath } from "../../../constants/routePaths";
import { Button, ButtonIcon } from "ui/gluestack/button";
import { ChevronRightIcon } from "ui/gluestack/icon";

export function MedicationLogTemplate() {
  const [filters, setFilters] = useState<MedicationLogsFilterForm>({
    date_from: undefined,
    date_to: undefined,
  });
  const filterModalRef = useRef<MedicationLogsFilterModalRef>(null);

  const {
    data: medicationReports,
    isFetching,
    meta,
    handlePageChange,
  } = useMedicationLogsWithOffline(filters);

  const columns: ColumnModel<MedicationReportModel>[] = [
    {
      title: "Medication",
      dataKey: "medication",
      renderCell: (medication: MedicationReportModel) => (
        <View className="flex flex-row items-center">
          <MedicationLogContent medication={medication} />
        </View>
      ),
    },
    {
      title: "Status",
      dataKey: "status",
      renderCell: (medication: MedicationReportModel) => {
        const isAdministered =
          medication.status === MedicationAdministrationStatusEnum.ADMINISTERED;
        const isSkipped =
          medication.status === MedicationAdministrationStatusEnum.SKIPPED;

        const hasOriginalNote = Boolean(medication.other_information);
        const hasPostSubmissionNotes =
          medication.post_submission_notes.length > 0;

        let noteLabel = "No notes provided";

        if (hasOriginalNote && !hasPostSubmissionNotes) {
          noteLabel = "View Note";
        } else if (!hasOriginalNote && hasPostSubmissionNotes) {
          noteLabel = "View Post Submission Notes";
        } else if (hasOriginalNote && hasPostSubmissionNotes) {
          noteLabel = "View Notes";
        }

        const statusColourClass =
          statusToColourClassMap[medication.status] || "text-gray-600";

        return (
          <View className="gap-1">
            <Text
              className={`uppercase text-xs font-semibold ${statusColourClass}`}
            >
              {snakeToSentence(medication.status)}
            </Text>

            {isAdministered ? (
              <Text className="italic">{noteLabel}</Text>
            ) : isSkipped ? (
              <Text>{medication.skip_reason}</Text>
            ) : null}
          </View>
        );
      },
    },
    {
      title: "Actioned by",
      dataKey: "administrator",
      renderCell: (medication: MedicationReportModel) => (
        <View className="flex flex-row items-center">
          <Text className="font-medium">
            {getFullName(medication?.administrator)}
            <MedicationTimeContent time={medication.created_at} />
          </Text>
        </View>
      ),
    },
    {
      renderCell: (medication: MedicationReportModel) => {
        return (
          <Button
            variant="link"
            size="sm"
            onPress={() =>
              `${ResidentsPath}/${medication.resident.id}/medications/log/${medication.id}`
            }
          >
            <ButtonIcon as={ChevronRightIcon} />
          </Button>
        );
      },
      colSpan: 1,
    },
  ];

  const handleFilterPress = () => {
    filterModalRef.current?.show(filters);
  };

  const handleApplyFilters = (newFilters: MedicationLogsFilterForm) => {
    setFilters(newFilters);
  };

  const hasFilters = !!(filters.date_from || filters.date_to);

  return (
    <View>
      <View className="flex flex-row justify-between items-center mb-4">
        <Text className="text-lg font-semibold">Medication Logs</Text>
        <FilterButton onPress={handleFilterPress} hasFilters={hasFilters} />
      </View>
      <TableList
        columns={columns}
        data={medicationReports || []}
        isFetching={isFetching}
        emptyMessage="No logs available."
        mobileRenderItem={(item) => (
          <CardItem
            key={item.id}
            cardContent={<ReportCardContent medication={item} showLabel />}
          />
        )}
        clickablePath={(medicationLog) =>
          `${ResidentsPath}/${medicationLog.resident.id}/medications/log/${medicationLog.id}`
        }
      />
      <Pagination meta={meta} onPageChange={handlePageChange} />
      <MedicationLogsFilterModal
        ref={filterModalRef}
        onApplyFilters={handleApplyFilters}
      />
    </View>
  );
}
